import React, { useEffect } from "react";
import { Box, Divider, Stack } from "@mui/material";
import { SSText } from "src/components/base/components/SSText";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { AttendanceSeatDropdown } from "./AttendanceSeatDropdown";
import { useAttendanceModal } from "./EventAttendanceModal";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import {
  calcEventRemainingSeatCount,
  getReservationSeatCap,
  pickCurrentMonthPass,
} from "./utils.event-attendance";

interface AttendanceSeatSelectorProps {
  isDisabled?: boolean;
}

export function AttendanceSeatSelector(props: AttendanceSeatSelectorProps) {
  const theme = useSSTheme();
  const attendanceModal = useAttendanceModal();
  const { HOME_PAGE_SETTINGS } = useTeamConfig();
  const { passesOnAccount } = useGlobalStore();
  // Get data internally
  const event = attendanceModal?.event;
  const seatCount = attendanceModal?.seatCount;
  const passSeatCount = event?.seatInformation?.PASS || 0;
  const optedInSeatCount = event?.optedInSeatCount || 1;

  // Scope the reservable seats to the user's current month-pass (instead of
  // summing across every pass they hold) and clamp by that pass's
  // totalAvailableSeatsForReservation cap. Reuses the same month-pass pick the
  // reservation limit uses; the hold path re-checks the cap server-side.
  const currentMonthPass = pickCurrentMonthPass(passesOnAccount);
  const scopedPassSeatCount = currentMonthPass?.seatCount ?? passSeatCount;
  const reservationSeatCap = getReservationSeatCap(event);

  // Cap the dropdown at remaining event inventory AND the synced TM reservation
  // cap, so the user can't pick a seat count the hold will reject. When already
  // opted in, the user's existing seats were already deducted from both counts
  // (currentTotalClaimCount, and the reservation cap at reserve time); add them
  // back since they'd be released/re-held if the reservation changes. Without
  // this, an opted-in user whose cap hit 0 sees an empty dropdown and submitting
  // 0 would wrongly return their seats and bump the cap back up.
  const currentReservedSeats = event?.optedIn ? event?.optedInSeatCount || 0 : 0;
  const eventRemainingSeatCount = calcEventRemainingSeatCount(event);
  const effectiveMaxSeatCount = Math.min(
    scopedPassSeatCount,
    (reservationSeatCap ?? Infinity) + currentReservedSeats,
    eventRemainingSeatCount + currentReservedSeats,
  );

  // For PAID reservations that are being CHANGED (the user already opted in),
  // seat count may only go UP — reducing seats would force a refund, which we
  // never want to have to do. Floor the dropdown at the currently reserved
  // seat count. New reservations and free events keep a floor of 1. Clamp to
  // the max so the dropdown always has at least the current option.
  const isPaidReservationChange =
    Boolean(event?.isPaymentRequired) && Boolean(event?.optedIn);
  const minSeatCount = isPaidReservationChange
    ? Math.min(event?.optedInSeatCount || 1, effectiveMaxSeatCount)
    : 1;

  const handleSeatCountChange = (newSeatCount: number) => {
    attendanceModal?.set?.({ seatCount: newSeatCount });
  };

  useEffect(() => {
    const initialSeatCount = event?.optedIn ? optedInSeatCount : passSeatCount;
    attendanceModal.set({
      seatCount: Math.min(initialSeatCount, effectiveMaxSeatCount),
    });
  }, []);

  return (
    <Stack mt={6} mb={3} spacing={3} width={"100%"}>
      <Box width={"100%"} position={"relative"}>
        <Divider />
        <SSText
          variant="body"
          fontWeight={700}
          fontSize={"1.1rem"}
          color={theme?.mui?.palette?.grey?.[800]}
          sx={{
            position: "absolute",
            top: "50%",
            left: "0",
            transform: "translateY(-50%)",
            backgroundColor: theme?.mui?.palette?.background?.paper,
            padding: "0 10px",
            textAlign: "left",
            pl: 0,
            width: "max-content",
            whiteSpace: "nowrap",
          }}
        >
          {HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.SEAT_COUNT_SELECTOR_TITLE ||
            "Select Seat Count"}
        </SSText>
      </Box>

      <Stack spacing={1}>
        <SSText
          variant="body"
          fontWeight={500}
          fontSize="1rem"
          color={theme.mui.palette.grey[700]}
        >
          Select Number of Seats
        </SSText>
        <Box width={"100%"}>
          <AttendanceSeatDropdown
            isDisabled={props?.isDisabled}
            min={minSeatCount}
            value={seatCount}
            max={effectiveMaxSeatCount}
            onChange={handleSeatCountChange}
          />
        </Box>
      </Stack>
    </Stack>
  );
}
